import React, { useMemo, useState, useEffect } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './PriorityInboxEngine.module.css';

const SortableCard = ({ id, text, index, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`${styles.card} ${isDragging ? styles.dragging : ''}`}
      initial={{ y: -50, opacity: 0, rotate: (index % 2 === 0 ? -1 : 1) * (index + 1) }}
      animate={{ y: 0, opacity: 1, rotate: 0 }}
      transition={{ delay: index * 0.08, type: 'spring', stiffness: 90, damping: 16 }}
      {...attributes}
      {...listeners}
    >
      <span className={styles.cardLabel}>{id}</span>
      <span className={styles.cardText}>{text}</span>
      <span className={styles.dragHandle}>⠿</span>
    </motion.div>
  );
};

const PriorityInboxEngine = ({ gamifiedQuestion, onAnswer, accent = '#264653' }) => {
  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 5) || [], [gamifiedQuestion?.options]);

  const byId = useMemo(
    () => options.reduce((acc, option) => {
      acc[option.label] = option;
      return acc;
    }, {}),
    [options]
  );

  const [items, setItems] = useState(options.map((option) => option.label));
  const [showOutcome, setShowOutcome] = useState(false);
  const [lockedLabel, setLockedLabel] = useState(null);
  const [hasDragged, setHasDragged] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);

  useEffect(() => {
    setItems(options.map((option) => option.label));
    const timer = setTimeout(() => setSceneReady(true), 300);
    return () => clearTimeout(timer);
  }, [options]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      return arrayMove(items, oldIndex, newIndex);
    });

    if (!hasDragged) setHasDragged(true);
  };

  const handleSubmit = () => {
    if (!hasDragged || lockedLabel) return;
    const topLabel = items[0];
    setLockedLabel(topLabel);
    setShowOutcome(true);
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.div
          className={styles.inboxScene}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <svg viewBox="0 0 400 140" className={styles.sceneSvg}>
            {/* Inbox tray visualization */}
            <motion.g
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {/* Tray back */}
              <rect x="80" y="50" width="240" height="70" rx="4" fill="#f5f5f5" stroke="#1a1a1a" strokeWidth="2" />
              {/* Papers in tray */}
              <motion.g
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <rect x="100" y="65" width="200" height="15" rx="2" fill="#fff" stroke="#e0e0e0" strokeWidth="1" />
                <rect x="105" y="85" width="190" height="15" rx="2" fill="#fff" stroke="#e0e0e0" strokeWidth="1" />
                <rect x="95" y="45" width="210" height="15" rx="2" fill="#fff" stroke="#e0e0e0" strokeWidth="1" />
              </motion.g>
              {/* Tray front edge */}
              <rect x="75" y="110" width="250" height="15" rx="4" fill="#e8e8e8" stroke="#1a1a1a" strokeWidth="2" />
              {/* Label */}
              <text x="200" y="30" textAnchor="middle" fontSize="12" fill="#666" fontWeight="500">INBOX</text>
            </motion.g>

            {/* Priority indicator arrow */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <text x="60" y="85" textAnchor="middle" fontSize="10" fill="#666">Most</text>
              <text x="60" y="98" textAnchor="middle" fontSize="10" fill="#666">Important</text>
              <line x1="75" y1="88" x2="95" y2="88" stroke="#666" strokeWidth="2" markerEnd="url(#arrowhead)" />
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
                </marker>
              </defs>
            </motion.g>
          </svg>
          <span className={styles.sceneLabel}>Drag to reorder — Most Important → Least</span>
        </motion.div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Draggable List */}
      <div className={styles.optionsAreaFull}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={rectSortingStrategy}>
            <div className={styles.cardsContainer}>
              {items.map((id, index) => (
                <SortableCard
                  key={id}
                  id={id}
                  text={byId[id]?.actionText || byId[id]?.text || ''}
                  index={index}
                  disabled={!!lockedLabel}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <AnimatePresence>
          {hasDragged && !lockedLabel && (
            <motion.button
              type="button"
              className={styles.confirmBtn}
              onClick={handleSubmit}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Confirm Priority Order
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={lockedLabel === 'A' ? 'success' : 'warning'}
        message={byId[lockedLabel]?.consequence || 'Priority inbox response saved.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (lockedLabel) onAnswer(lockedLabel);
        }}
      />
    </div>
  );
};

export default PriorityInboxEngine;
